import { useInfiniteScroll } from '@vueuse/core'

import type { Ref } from 'vue'
import type { DisciplineStageComment } from './interfaces'

import { DisciplineKanbanTab } from './enums'
import { useDisciplineCommentsStore } from './comments/store'
import { useDisciplineStore } from './store'
import { useDisciplineStageCommentsApi } from '../api/stage-comments.hook'
import { useDisciplineCommentsEvents } from './discipline-comments-events.hook'
import { useDisciplineContext } from './discipline-context.hook'
import { useDisciplineKanbanUrl } from './discipline-kanban-url.hook'

interface DisciplineCommentsReturn {
  refListElement: Ref<HTMLElement | null>
  refEditing: Ref<DisciplineStageComment | null>
  refKanbanUrl: ComputedRef<string | null>
  comments: ComputedRef<DisciplineStageComment[]>
  hasStage: ComputedRef<boolean>
  isLoadingMore: ComputedRef<boolean>
  fetchComments: () => Promise<void>
  setEditComment: (comment: DisciplineStageComment | null) => void
  setSavedComment: (comment: DisciplineStageComment) => void
}

export const useDisciplineComments = (): DisciplineCommentsReturn => {
  const route = useRoute()
  const store = useDisciplineCommentsStore()
  const disciplineStore = useDisciplineStore()
  const { whenReady } = useDisciplineContext()

  useDisciplineCommentsEvents()

  const refListElement = ref<HTMLElement | null>(null)
  const refEditing = ref<DisciplineStageComment | null>(null)
  const disciplineId = (): string => route.params.id as string

  const refKanbanUrl = useDisciplineKanbanUrl(() => disciplineStore.getDiscipline, DisciplineKanbanTab.Comments)
  const comments = computed<DisciplineStageComment[]>(() => store.getComments)
  /** A further page only: the first one is under the page loader. */
  const isLoadingMore = computed<boolean>(() => store.isLoading && store.getComments.length > 0)
  /** The legacy card listed and took comments only while the discipline sits on a kanban stage. */
  const hasStage = computed<boolean>(() => !!disciplineStore.getDiscipline?.stage)

  const fetchComments = async (): Promise<void> => {
    // The stage that decides whether there are comments at all comes with the shared discipline read.
    await whenReady(disciplineId())
    if (!hasStage.value) return

    await useDisciplineStageCommentsApi({ disciplineId: disciplineId() })
  }

  const loadMore = async (): Promise<void> => {
    const cursor = store.getNextCursor
    if (!cursor) return

    await useDisciplineStageCommentsApi({ disciplineId: disciplineId(), cursor })
  }

  const setEditComment = (comment: DisciplineStageComment | null): void => {
    refEditing.value = comment
  }

  const setSavedComment = (comment: DisciplineStageComment): void => {
    store.upsertComment(comment)
    refEditing.value = null
  }

  useInfiniteScroll(refListElement, loadMore, {
    distance: 100,
    canLoadMore: () => store.hasMore && !store.isLoading,
  })

  return {
    refListElement,
    refEditing,
    refKanbanUrl,
    comments,
    hasStage,
    isLoadingMore,
    fetchComments,
    setEditComment,
    setSavedComment,
  }
}
